import {A11yModule} from '@angular/cdk/a11y';
import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, NgModule, provideAppInitializer} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {DcpAuthInterceptor} from '@app/auth/interceptors/auth.interceptor';
import {DataTrackingInterceptor} from '@app/http-interceptors/data-tracking-interceptor/data-tracking.interceptor';
import {DcpHeaderComponent} from '@app/layout/header/component/dcp-header.component';
import {ExpressCheckoutPageModule} from '@app/pages/express-checkout/express-checkout-page/express-checkout-page.module';
import {SapEmployeePopupLoginListenerService} from '@app/sap-employee-popup/sap-employee-popup-login-listener.service';
import {DcpProductSearchService} from '@app/search/services/dcp-product-search.service';
import {DcpCommonAnalyticsModule} from '@app/services/analytics/dcp-common-analytics.module';
import {DCPAnonymousConsentsInterceptor} from '@app/services/consents/dcp-anonymous-consents-interceptor';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpCurrencyService} from '@app/services/pricing/dcp-currency.service';
import {
  ChatAdvisorContextService,
  ChatAdvisorPageService,
  CommercialModelService,
  ContactUsButtonComponent,
  ContactUsLinkComponent,
  DcpChatAdvisorComponent,
  DCPOccConfiguratorVariantAddToCartSerializer,
  DcpOccConfiguratorVariantUpdateCartEntrySerializer,
  DcpOccEndpointsService,
  FeatureTogglesService,
  IconButtonTooltipComponent,
} from '@dcp/configurator';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import '@sapudex/web-components/dist/Assets.js';
import '@sapudex/web-components/dist/Avatar.js';
import '@sapudex/web-components/dist/Button.js';
import '@sapudex/web-components/dist/Link.js';
import '@sapudex/web-components/dist/MessageStrip.js';
import '@sapudex/web-components/dist/Modal.js';
import '@sapudex/web-components/dist/Search.js';
import '@sapudex/web-components/dist/TextField.js';
import '@sapudex/web-components/dist/Tile.js';
import '@sapudex/web-components/dist/Toast.js';
import '@sapudex/web-components/dist/HeroBanner.js';
import {CartPageLayoutHandler} from '@spartacus/cart/base/components';
import {ActiveCartService, MultiCartService} from '@spartacus/cart/base/core';
import {ProfileTagPushEventsService, TrackingService} from '@spartacus/cds';
import {CheckoutPaymentAdapter, PAYMENT_DETAILS_SERIALIZER} from '@spartacus/checkout/base/core';
import {
  AuthHttpHeaderService,
  AuthInterceptor,
  AuthService,
  FeaturesConfigModule,
  LanguageService,
  OccEndpointsService,
  ProductPageMetaResolver,
  ProductSearchService,
  provideConfig,
  SearchPageMetaResolver,
} from '@spartacus/core';
import {
  OccConfiguratorVariantAddToCartSerializer,
  OccConfiguratorVariantUpdateCartEntrySerializer,
  VARIANT_CONFIGURATOR_ADD_TO_CART_SERIALIZER,
  VARIANT_CONFIGURATOR_UPDATE_CART_ENTRY_SERIALIZER,
} from '@spartacus/product-configurator/rulebased';
import {
  AppRoutingModule,
  JsonLdBuilderModule,
  OutletModule,
  OutletRefModule,
  PageSlotModule,
  ProductListComponentService,
  PwaModule,
  SeoModule,
  ViewConfig,
} from '@spartacus/storefront';
import {PersonalizationContextService} from '@spartacus/tracking/personalization/core';
import {UserAccountAdapter, UserAccountConnector} from '@spartacus/user/account/core';
import {OccUserAccountAdapter} from '@spartacus/user/account/occ';
import '@ui5/webcomponents-icons/dist/accept.js';
import '@ui5/webcomponents-icons/dist/checklist-item-2.js';
import '@ui5/webcomponents-icons/dist/decline.js';
import '@ui5/webcomponents-icons/dist/delete.js';
import '@ui5/webcomponents-icons/dist/edit.js';
import '@ui5/webcomponents-icons/dist/favorite.js';
import '@ui5/webcomponents-icons/dist/feedback.js';
import '@ui5/webcomponents-icons/dist/history.js';
import '@ui5/webcomponents-icons/dist/hint.js';
import '@ui5/webcomponents-icons/dist/message-information.js';
import '@ui5/webcomponents-icons/dist/person-placeholder.js';
import '@ui5/webcomponents-icons/dist/product.js';
import '@ui5/webcomponents-icons/dist/puzzle.js';
import '@ui5/webcomponents-icons/dist/sap-logo-shape.js';
import '@ui5/webcomponents-icons/dist/search.js';
import '@ui5/webcomponents-icons/dist/unwired.js';
import '@ui5/webcomponents-icons/dist/warning.js';
import '@ui5/webcomponents-icons/dist/world.js';
import '@ui5/webcomponents/dist/Icon.js';
import '@ui5/webcomponents/dist/Title.js';
import {setLanguage} from '@ui5/webcomponents-base/dist/config/Language.js';
import {LanguageSupportService} from '@app/services/i18n/language-support.service';
import {environment} from '../environments/environment';
import {AuthModule} from './auth/auth.module';
import {DcpAuthHttpHeaderService} from './auth/services/dcp-auth-http-header.service';
import {DcpAuthService} from './auth/services/dcp-auth.service';
import {CartLayoutHandlerService} from './cart/cart-layout-handler.service';
import {CartModule} from './cart/cart.module';
import {DcpRoutingModule} from './dcp-routing.module';

import {GlobalErrorHandler} from './core/components/error/global-error-handler';
import {DcpGlobalMessageComponentModule} from './global-message/global-message.module';
import {httpInterceptorProviders} from './http-interceptors';
import {LayoutModule} from './layout/layout.module';
import {DcpCheckoutPaymentAdapterService} from './payment/services/dcp-checkout-payment-adapter.service';
import {DcpPersonalizationContextService} from './personalization/dcp-personalization-context.service';
import {configuratorProviderList} from './product/configurator/providers';
import {ProductModule} from './product/product.module';
import {DcpProductPageMetaResolver} from './product/services/dcp-product-page-meta.resolver';
import {DCPSearchPageMetaResolver} from './product/services/search-page-meta.resolver';
import {DcpProfileTagPushEventsService} from './profiletag/dcp-profile-tag-push-events.service';
import {RegistrationFeatureModule} from './spartacus/features/registration/registration-feature.module';
import {SearchFeatureModule} from './spartacus/features/search/search-feature.module';
import {PaymentFeatureModule} from './spartacus/features/payment/payment-feature.module';
import {PaymentStoreModule} from './payment/payment-store.module';
import {DigitalNativeCheckoutFeatureModule} from './spartacus/features/digital-native-checkout/digital-native-checkout-feature.module';
import {DcpPageMetaConfig} from './config/DcpPageMetaConfig';
import {CustomProductListComponentService} from './search/services/custom-product-list-component.service';
import {DcpMultiCartService} from './services/cart/dcp-multi-cart.service';
import {DcpPaymentDetailsSerializer} from './services/dcp-payment-details-serializer';
import {DcpTrackingService} from './services/dcp-tracking.service';

import {FormlyDcpModule} from './shared/formly/formly-dcp.module';

import {DcpPageIndexModule} from './shared/page-index/dcp-page-index.module';
import {SelectTypeB2BModule} from './shared/select-type/select-type.module';
import {DcpSupportFormModule} from './shared/support-form/dcp-support-form.module';
import {FixesModule} from './spartacus/fixes/fixes.module';
import {SpartacusModule} from './spartacus/spartacus.module';
import {ConcurLaunchTokenService} from '@app/services/concur/concur-launch-token.service';
import {RefQuoteIdService} from '@app/services/reference-quote/ref-quote-id.service';
import {DcpLanguageService} from '@app/services/i18n/dcp-language.service';
import {DcpPageLayoutModule} from './temp/page-layout/page-layout.module';
import {TestModule} from './test/test.module';

import {ToastsComponent} from '@app/global-message/toasts/toasts.component';
import {DcpUdexFooterComponent} from '@app/layout/footer/dcp-udex-footer/dcp-udex-footer.component';
import {RedundantHomepageComponent} from './redundant-homepage/redundant-homepage.component';
import {SharedWidgetsModule} from './shared/shared-widgets/shared-widgets.module';

const devImports: any[] = [];
if (!environment.production) {
  devImports.push(StoreDevtoolsModule.instrument({connectInZone: true}));
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    DcpRoutingModule,
    AppRoutingModule,
    A11yModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    CommonModule,
    ReactiveFormsModule,
    // SPARTACUS
    SpartacusModule,
    PwaModule,
    SeoModule,
    OutletModule,
    OutletRefModule,
    PageSlotModule,
    FeaturesConfigModule,
    DcpGlobalMessageComponentModule,
    JsonLdBuilderModule,
    FixesModule,
    // DCP
    AuthModule,
    CartModule,
    PaymentStoreModule,
    DigitalNativeCheckoutFeatureModule,
    RedundantHomepageComponent,
    LayoutModule,
    PaymentFeatureModule,
    ProductModule,
    RegistrationFeatureModule,
    SearchFeatureModule,
    DcpPageLayoutModule,
    TestModule,
    ExpressCheckoutPageModule,
    // Analytics
    DcpCommonAnalyticsModule,
    // SHARED
    DcpPageIndexModule,
    FormlyDcpModule,
    DcpSupportFormModule,
    SelectTypeB2BModule,
    IconButtonTooltipComponent,
    ContactUsButtonComponent,
    ContactUsLinkComponent,
    ...devImports,
    DcpChatAdvisorComponent,
    DcpHeaderComponent,
    DcpUdexFooterComponent,
    ToastsComponent,
    SharedWidgetsModule
  ],
  providers: [
    ChatAdvisorContextService,
    ChatAdvisorPageService,
    FeatureTogglesService,
    DcpCurrencyService,
    httpInterceptorProviders,
    OccUserAccountAdapter,
    UserAccountConnector,
    provideAppInitializer(() => {
      inject(ConcurLaunchTokenService).captureFromUrl();
    }),
    provideAppInitializer(() => {
      inject(RefQuoteIdService).captureFromUrl();
    }),
    provideAppInitializer(() => {
      inject(SapEmployeePopupLoginListenerService);
    }),
    provideAppInitializer(() => {
      const initializerFn = ((fts: FeatureTogglesService, commercialModelService: CommercialModelService) => () => {
        fts.fetchFeatureToggles();
        commercialModelService.getFullCommercialModelsList().subscribe();
      })(inject(FeatureTogglesService), inject(CommercialModelService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const languageSupportService = inject(LanguageSupportService);
      inject(LanguageService)
        .getActive()
        .subscribe((iso) => setLanguage(languageSupportService.toSupportedLanguage(iso)));
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DataTrackingInterceptor,
      multi: true,
    },
    {provide: SearchPageMetaResolver, useExisting: DCPSearchPageMetaResolver},
    {
      provide: ProductPageMetaResolver,
      useExisting: DcpProductPageMetaResolver,
    },
    {provide: AuthService, useExisting: DcpAuthService},
    {provide: AuthInterceptor, useExisting: DcpAuthInterceptor},
    {
      provide: ProductListComponentService,
      useExisting: CustomProductListComponentService,
    },
    provideConfig({view: {defaultPageSize: 12}} as ViewConfig),
    {provide: CartPageLayoutHandler, useExisting: CartLayoutHandlerService},
    {
      provide: PersonalizationContextService,
      useExisting: DcpPersonalizationContextService,
    },
    {
      provide: ProfileTagPushEventsService,
      useExisting: DcpProfileTagPushEventsService,
    },
    {
      provide: CheckoutPaymentAdapter,
      useExisting: DcpCheckoutPaymentAdapterService,
    },
    {provide: UserAccountAdapter, useExisting: OccUserAccountAdapter},
    {
      provide: VARIANT_CONFIGURATOR_ADD_TO_CART_SERIALIZER,
      useExisting: DCPOccConfiguratorVariantAddToCartSerializer,
      multi: true,
    },
    {
      provide: VARIANT_CONFIGURATOR_UPDATE_CART_ENTRY_SERIALIZER,
      useExisting: DcpOccConfiguratorVariantUpdateCartEntrySerializer,
      multi: true,
    },
    {
      provide: OccConfiguratorVariantAddToCartSerializer,
      useExisting: DCPOccConfiguratorVariantAddToCartSerializer,
    },
    {
      provide: OccConfiguratorVariantUpdateCartEntrySerializer,
      useExisting: DcpOccConfiguratorVariantUpdateCartEntrySerializer,
    },
    {
      provide: PAYMENT_DETAILS_SERIALIZER,
      useExisting: DcpPaymentDetailsSerializer,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DCPAnonymousConsentsInterceptor,
      multi: true,
    },
    {provide: OccEndpointsService, useExisting: DcpOccEndpointsService},
    {provide: AuthHttpHeaderService, useExisting: DcpAuthHttpHeaderService},
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    provideConfig(DcpPageMetaConfig),
    {provide: ActiveCartService, useExisting: DcpActiveCartService},
    {provide: MultiCartService, useExisting: DcpMultiCartService},
    {provide: TrackingService, useExisting: DcpTrackingService},
    {provide: ProductSearchService, useExisting: DcpProductSearchService},
    {provide: LanguageService, useClass: DcpLanguageService},
    ...configuratorProviderList,
    provideConfig({
      cmsComponents: {
        RedundantHomepageComponent: {
          component: RedundantHomepageComponent,
        },
      },
    }),
  ],
})
export class AppModule {}
